import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { AuthGuard } from '@/features/auth/components/AuthGuard'
import { AuthProvider } from '@/features/auth/components/AuthProvider'
import { ErrorBoundary } from '@/shared/components/feedback/ErrorBoundary'
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner'
import { ROUTES } from '@/shared/constants/routes'
import { Toaster } from '@/shared/components/ui/Toaster'

// Lazy-load all page-level components for code splitting
const AuthPage = lazy(() => import('@/pages/public/AuthPage'))

// Member pages
const DashboardPage = lazy(() => import('@/pages/member/DashboardPage'))
const BookPage = lazy(() => import('@/pages/member/BookPage'))
const CapacityPage = lazy(() => import('@/pages/member/CapacityPage'))
const EventsPage = lazy(() => import('@/pages/member/EventsPage'))
const EventDetailPage = lazy(() => import('@/pages/member/EventDetailPage'))
const CommunityPage = lazy(() => import('@/pages/member/CommunityPage'))
const RewardsPage = lazy(() => import('@/pages/member/RewardsPage'))
const ThreadDetailPage = lazy(() => import('@/pages/member/ThreadDetailPage'))
const CreateThreadPage = lazy(() => import('@/pages/member/CreateThreadPage'))
const AccessQRPage = lazy(() => import('@/pages/member/AccessQRPage'))
const NotificationsPage = lazy(() => import('@/pages/member/NotificationsPage'))
const ProfilePage = lazy(() => import('@/pages/member/ProfilePage'))
const WalletPage = lazy(() => import('@/pages/member/WalletPage'))

// Admin pages
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'))
const AdminMembersPage = lazy(() => import('@/pages/admin/AdminMembersPage'))
const AdminBookingsPage = lazy(() => import('@/pages/admin/AdminBookingsPage'))
const AdminEventsPage = lazy(() => import('@/pages/admin/AdminEventsPage'))
const AdminCapacityPage = lazy(() => import('@/pages/admin/AdminCapacityPage'))
const AdminModerationPage = lazy(() => import('@/pages/admin/AdminModerationPage'))

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}

function MemberErrorFallback() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 text-center">
      <p className="text-lg font-medium text-foreground">Something went wrong</p>
      <p className="text-sm text-muted-foreground mt-2">Please try refreshing the page.</p>
      <button
        className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        onClick={() => window.location.reload()}
      >
        Refresh
      </button>
    </div>
  )
}

function AdminErrorFallback() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 text-center">
      <p className="text-lg font-medium text-foreground">Admin panel error</p>
      <p className="text-sm text-muted-foreground mt-2">An error occurred loading this section. Please try refreshing.</p>
      <button
        className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        onClick={() => window.location.reload()}
      >
        Refresh
      </button>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path={ROUTES.LANDING} element={<Navigate to={ROUTES.AUTH} replace />} />
              <Route path={ROUTES.AUTH} element={<AuthPage />} />

              {/* Member routes — requires authenticated user (membership gate deferred to Sprint 2) */}
              <Route element={<AuthGuard requiredRole="member" />}>
                <Route path={ROUTES.DASHBOARD} element={<ErrorBoundary fallback={<MemberErrorFallback />}><DashboardPage /></ErrorBoundary>} />
                <Route path={ROUTES.BOOK} element={<ErrorBoundary fallback={<MemberErrorFallback />}><BookPage /></ErrorBoundary>} />
                <Route path={ROUTES.CAPACITY} element={<ErrorBoundary fallback={<MemberErrorFallback />}><CapacityPage /></ErrorBoundary>} />
                <Route path={ROUTES.EVENTS} element={<ErrorBoundary fallback={<MemberErrorFallback />}><EventsPage /></ErrorBoundary>} />
                <Route path="/app/events/:id" element={<ErrorBoundary fallback={<MemberErrorFallback />}><EventDetailPage /></ErrorBoundary>} />
                <Route path={ROUTES.COMMUNITY} element={<ErrorBoundary fallback={<MemberErrorFallback />}><CommunityPage /></ErrorBoundary>} />
                <Route path={ROUTES.REWARDS} element={<ErrorBoundary fallback={<MemberErrorFallback />}><RewardsPage /></ErrorBoundary>} />
                <Route path={ROUTES.WALLET} element={<ErrorBoundary fallback={<MemberErrorFallback />}><WalletPage /></ErrorBoundary>} />
                <Route path="/app/community/new" element={<ErrorBoundary fallback={<MemberErrorFallback />}><CreateThreadPage /></ErrorBoundary>} />
                <Route path="/app/community/:id" element={<ErrorBoundary fallback={<MemberErrorFallback />}><ThreadDetailPage /></ErrorBoundary>} />
                <Route path={ROUTES.ACCESS_QR} element={<ErrorBoundary fallback={<MemberErrorFallback />}><AccessQRPage /></ErrorBoundary>} />
                <Route path={ROUTES.NOTIFICATIONS} element={<ErrorBoundary fallback={<MemberErrorFallback />}><NotificationsPage /></ErrorBoundary>} />
                <Route path={ROUTES.PROFILE} element={<ErrorBoundary fallback={<MemberErrorFallback />}><ProfilePage /></ErrorBoundary>} />
              </Route>

              {/* Admin routes — requires admin role */}
              <Route element={<AuthGuard requiredRole="admin" />}>
                <Route path={ROUTES.ADMIN} element={<Navigate to={ROUTES.ADMIN_DASHBOARD} replace />} />
                <Route path={ROUTES.ADMIN_DASHBOARD} element={<ErrorBoundary fallback={<AdminErrorFallback />}><AdminDashboardPage /></ErrorBoundary>} />
                <Route path={ROUTES.ADMIN_MEMBERS} element={<ErrorBoundary fallback={<AdminErrorFallback />}><AdminMembersPage /></ErrorBoundary>} />
                <Route path={ROUTES.ADMIN_BOOKINGS} element={<ErrorBoundary fallback={<AdminErrorFallback />}><AdminBookingsPage /></ErrorBoundary>} />
                <Route path={ROUTES.ADMIN_EVENTS} element={<ErrorBoundary fallback={<AdminErrorFallback />}><AdminEventsPage /></ErrorBoundary>} />
                <Route path={ROUTES.ADMIN_CAPACITY} element={<ErrorBoundary fallback={<AdminErrorFallback />}><AdminCapacityPage /></ErrorBoundary>} />
                <Route path={ROUTES.ADMIN_MODERATION} element={<ErrorBoundary fallback={<AdminErrorFallback />}><AdminModerationPage /></ErrorBoundary>} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to={ROUTES.AUTH} replace />} />
            </Routes>
          </Suspense>
          <Toaster />
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  )
}
